import {
  CATEGORY,
  COOKIE_REGISTRY,
  adsDataRedaction,
  consentModeSignals,
  emptyConsent,
  revokedCookies,
} from '@/constants/cookie-registry'
import { readConsentCategories, refreshConsentState } from './state'

export * from './state'

function domainCandidates() {
  const parts = String(location.hostname || '').split('.')
  const candidates = ['']

  for (let i = 0; i < parts.length - 1; i++) {
    const domain = parts.slice(i).join('.')
    candidates.push(domain, '.' + domain)
  }

  return candidates
}

function deleteCookie(name) {
  const expired = 'Thu, 01 Jan 1970 00:00:01 GMT'

  domainCandidates().forEach((domain) => {
    const suffix = domain ? '; domain=' + domain : ''
    document.cookie = name + '=; expires=' + expired + '; path=/' + suffix
    document.cookie = name + '=; expires=' + expired + '; path=/' + suffix + '; secure; samesite=lax'
  })
}

function presentCookieNames() {
  try {
    return document.cookie
      .split('; ')
      .map((row) => row.split('=')[0])
      .filter(Boolean)
  } catch (e) {
    return []
  }
}

function matchingCookieNames(entry) {
  if (entry.match === 'prefix') {
    return presentCookieNames().filter((name) => name.indexOf(entry.name) === 0)
  }
  return presentCookieNames().filter((name) => name === entry.name)
}

// Consent Mode stops the next tag from firing; it deletes nothing that already exists. A
// visitor who consented and then withdrew keeps every cookie already in the browser unless
// this runs. It only reaches our own domain and its subdomains - a cookie written by
// .doubleclick.net or .tiktok.com is out of reach of any script of ours, and the only way to
// be rid of those is for the tag never to have fired.
export function clearRevokedCookies(consent) {
  revokedCookies(consent).forEach((entry) => {
    matchingCookieNames(entry).forEach(deleteCookie)
  })
}

// Events the app pushed before the visitor decided. dataLayer is deliberately left alone: the
// container is already on the page and consumed every entry as it arrived, so rewriting the
// array afterwards changes nothing - what keeps those events away from a vendor is the denied
// consent state, not the queue. fbq is the exception. Its stub queue is still ours until the
// container's Meta tag swaps in the real pixel, and it would be replayed the moment it does.
function dropPendingEvents() {
  const fbq = window.fbq

  if (!fbq || typeof fbq.callMethod === 'function') return
  if (!Array.isArray(fbq.queue)) return

  fbq.queue.length = 0
}

function updateVendorConsent(consent) {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', consentModeSignals(consent))
    window.gtag('set', 'ads_data_redaction', adsDataRedaction(consent))
  }

  // The Meta Pixel is a container tag and reads no Consent Mode signal. Its consent check
  // belongs in GTM, but queueing this costs nothing and is the one lever we still have if
  // the tag ships without one.
  if (window.fbq) {
    window.fbq('consent', consent[CATEGORY.MARKETING] ? 'grant' : 'revoke')
  }
}

// Conversion events the app sends itself. The Meta Pixel reads no Consent Mode signal, so
// nothing in the container guarantees this call is gated - the only thing that does is an
// Additional consent check on the tag, which lives outside this repo and has gone missing
// once already when Meta reissued the tag template. Routing every fbq() through here keeps
// the gate in code as well, where a change to the container cannot silently remove it.
export function trackMeta(event, params) {
  if (!readConsentCategories()[CATEGORY.MARKETING]) return
  if (typeof window.fbq !== 'function') return

  window.fbq('track', event, params)
}

export function applyConsent(consent, options) {
  const state = consent || emptyConsent()

  if (options && options.dropQueues) {
    dropPendingEvents()
  }

  updateVendorConsent(state)
  clearRevokedCookies(state)

  return state
}

// public/index.html already pushes the stored state to Consent Mode before the container
// loads - it has to, nothing here runs early enough. This re-asserts the same state once the
// app is up and, more usefully, sweeps cookies left over from a consent that has since
// lapsed or been narrowed in another tab. It also restarts the consent cookie's 400-day cap,
// which is the only thing keeping the chain alive across a year of visits.
export function initConsent() {
  refreshConsentState()

  return applyConsent(readConsentCategories())
}

export { COOKIE_REGISTRY }
